import { Link } from "react-router-dom";
import { STATUS_META } from "@/lib/mh";
import { money } from "@/lib/onb";

export const FILTERS = [["all", "All"], ["w1", "Wave 1"], ["w2", "Wave 2"], ["w3", "Wave 3"], ["off", "Off for you"], ["needs", "Needs input"]];
export const WAVE_DASH = { 1: undefined, 2: "3 2", 3: "1 2.5" };

export const ModeSwitch = ({ mode, setMode, tailored }) => (
  tailored ? (
    <div className="flex p-1 rounded-full bg-white/[0.04] border border-white/10 w-fit" data-testid="sky-mode-switch">
      {[["standard", "Standard"], ["tailored", "Tailored"]].map(([k, t]) => (
        <button key={k} onClick={() => setMode(k)} data-testid={`sky-mode-${k}`}
          className={`h-8 px-4 rounded-full text-xs transition-colors ${mode === k ? "bg-white text-black" : "text-muted-foreground hover:text-foreground"}`}>{t}</button>
      ))}
    </div>
  ) : <Link to="/build" className="font-mono text-[11px] underline text-muted-foreground hover:text-foreground" data-testid="sky-build-link">Build your map to tailor this sky →</Link>
);

export const FilterChips = ({ filter, setFilter }) => (
  <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="sky-filters">
    {FILTERS.map(([k, t]) => (
      <button key={k} onClick={() => setFilter(k)} data-testid={`sky-filter-${k}`}
        className={`h-7 px-3 rounded-full border text-[11px] whitespace-nowrap transition-colors ${filter === k ? "border-[#D4FF3A]/60 text-[#D4FF3A] bg-[#D4FF3A]/10" : "border-white/10 text-muted-foreground hover:text-foreground"}`}>{t}</button>
    ))}
  </div>
);

const Ring = ({ dash }) => (
  <svg width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="8" r="3" fill="#9A9AAE" /><circle cx="8" cy="8" r="6.2" fill="none" stroke="#E8E8EE" strokeWidth="1.2" strokeDasharray={dash} /></svg>
);

export const Legend = ({ tailored }) => (
  <div className="flex gap-x-4 gap-y-1 overflow-x-auto font-mono text-[10px] uppercase tracking-wider text-muted-foreground whitespace-nowrap" data-testid="sky-legend">
    {Object.entries(STATUS_META).map(([k, m]) => <span key={k} className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: m.color }} />{m.label}</span>)}
    {tailored && <>
      <span className="flex items-center gap-1"><Ring />wave 1</span>
      <span className="flex items-center gap-1"><Ring dash="3 2" />wave 2</span>
      <span className="flex items-center gap-1"><Ring dash="1 2.5" />wave 3</span>
      <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full border border-white/40" />off</span>
      <span className="text-[#9A9AAE]">size = value</span>
    </>}
  </div>
);

export const StatusChip = ({ job, deps }) => {
  if (job?.status !== "running") return null;
  const entries = Object.entries(job.depts || {});
  const done = entries.filter(([, s]) => s === "done").length;
  const running = entries.filter(([, s]) => s === "running").map(([d]) => deps.find((x) => x.slug === d)?.name || d);
  return (
    <div className="absolute top-4 left-4 flex items-center gap-2 h-8 px-3.5 rounded-full border border-[#00E5FF]/40 bg-[#0A0A0F]/90 backdrop-blur text-xs mh-enter" data-testid="sky-status-chip">
      <span className="w-2 h-2 rounded-full bg-[#00E5FF] animate-pulse" />
      Tailoring {running.join(", ") || "…"}… <span className="text-muted-foreground">{done} of {entries.length} departments done</span>
    </div>
  );
};

const FIT = { relevant: "Relevant", maybe: "Maybe", not_relevant: "Not relevant" };

export const Tooltip = ({ n, mode, hint }) => {
  const t = mode === "tailored" ? n.tailored : null;
  const v = t?.value;
  return (
    <div className="absolute left-4 bottom-4 right-4 sm:right-auto sm:max-w-sm rounded-lg border border-white/10 bg-[#0A0A0F]/95 backdrop-blur-xl p-3 pointer-events-none mh-enter" role="tooltip" aria-live="polite" data-testid="constellation-tooltip">
      <div className="font-mono text-[10px] uppercase tracking-wider" style={{ color: n.hex }}>{n.stage} · {STATUS_META[n.runtime?.status]?.label || "Not started"}{t?.wave ? ` · wave ${t.wave}` : ""}</div>
      <div className="font-medium mt-1">{n.name}</div>
      <div className="text-xs text-muted-foreground mt-0.5" data-testid="tooltip-job">{t ? n.one_liner : n.tailored?.standard_one_liner || n.one_liner}</div>
      {t && (
        <div className="mt-2 space-y-1 text-xs" data-testid="tooltip-tailored">
          {!t.enabled && <div className="text-[#FFB020]">{t.fit === "not_relevant" ? "Scored not relevant" : "Switched off by hand"}</div>}
          {v && t.enabled && <div><span className="text-[#D4FF3A]">{money(v.money_low)}–{money(v.money_high)}</span><span className="text-muted-foreground"> projected / month</span></div>}
          <div className="text-muted-foreground">Owner: <span className="text-foreground">{t.owner_email || "—"}</span></div>
          <div className="text-muted-foreground">Fit: <span className="text-foreground">{FIT[t.fit] || t.fit}</span>{t.fit_reason ? ` · ${t.fit_reason}` : ""}</div>
        </div>
      )}
      {hint && <div className="font-mono text-[10px] text-[#00E5FF] mt-2">tap again to open</div>}
    </div>
  );
};

export const ViewSwitch = ({ view, setView }) => (
  <div className="flex p-1 rounded-full bg-white/[0.04] border border-white/10 w-fit" data-testid="sky-view-switch">
    {[["sky", "Sky"], ["map", "Map"]].map(([k, t]) => (
      <button key={k} onClick={() => setView(k)} data-testid={`sky-view-${k}`}
        className={`h-8 px-4 rounded-full text-xs transition-colors ${view === k ? "bg-[#D4FF3A] text-black" : "text-muted-foreground hover:text-foreground"}`}>{t}</button>
    ))}
  </div>
);

export const HubTip = ({ hb, tot }) => (
  <div className="absolute top-4 right-4 max-w-xs rounded-lg border bg-[#0A0A0F]/95 backdrop-blur-xl p-3 pointer-events-none mh-enter" style={{ borderColor: `${hb.hex}55` }} data-testid="sky-hub-tooltip">
    <div className="font-display text-2xl" style={{ color: hb.hex }}>{hb.name}</div>
    <div className="font-mono text-[10px] text-muted-foreground">{hb.tagline}</div>
    <div className="text-xs mt-2"><span className="text-foreground">{tot.active}</span><span className="text-muted-foreground"> of {tot.total} roles active</span></div>
    {tot.tailored && <div className="text-xs"><span className="text-[#D4FF3A]">${money(tot.money)}</span><span className="text-muted-foreground"> projected / month</span></div>}
    <div className="font-mono text-[10px] text-[#00E5FF] mt-2">click to zoom in →</div>
  </div>
);
